import React, { useEffect, useState } from "react";
import { useNotification } from "../../../../NotificationContext";
import LoaderComponent from "../../../../components/LoaderComponent";
import { format } from "date-fns";
import { getIPDVisitBilling } from "../../../../apis/visit";

interface IPDBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  patientId: number;
}

interface BillingData {
  pharmacy: Array<{
    id: number;
    sale_number: string;
    sale_date: string;
    items: Array<{
      name: string;
      quantity: number;
      selling_price: number;
      discount_percentage: number;
      tax_percentage: number;
      total: number;
    }>;
    total_amount: number;
    discount_amount: number;
    tax_amount: number;
    net_amount: number;
  }>;
  labOrders: Array<{
    id: number;
    order_number: string;
    order_date: string;
    items: Array<{
      name: string;
      fee: number;
    }>;
    total_amount: number;
  }>;
  services: Array<{
    id: number;
    name: string;
    fee: number;
    quantity: number;
    total: number;
  }>;
  patientBalance: {
    total_transactions: number;
    total_allocations: number;
    balance: number;
  };
}

const IPDBillingModal: React.FC<IPDBillingModalProps> = ({
  isOpen,
  onClose,
  visitId,
  patientId,
}) => {
  const { showNotification } = useNotification();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && visitId) {
      fetchBillingData();
    }
  }, [isOpen, visitId]);

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      const response = await getIPDVisitBilling(visitId);
      setBillingData(response as BillingData);
    } catch (error: any) {
      console.error("Failed to fetch billing data:", error);
      showNotification(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to load billing data",
        "topRight"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return "₹0.00";
    }
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) {
      return "₹0.00";
    }
    return `₹${num.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy");
    } catch (error) {
      return "-";
    }
  };

  const calculatePharmacyTotal = () => {
    if (!billingData) return 0;
    return billingData.pharmacy.reduce((sum, sale) => sum + (sale.net_amount || 0), 0);
  };

  const calculateLabTotal = () => {
    if (!billingData) return 0;
    return billingData.labOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
  };

  const calculateServicesTotal = () => {
    if (!billingData) return 0;
    return billingData.services.reduce((sum, service) => sum + (service.total || 0), 0);
  };

  const calculateGrandTotal = () => {
    return calculatePharmacyTotal() + calculateLabTotal() + calculateServicesTotal();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block w-full max-w-6xl overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:align-middle">
          <div className="flex justify-between p-4 text-white bg-primary">
            <span className="text-lg font-bold">Provisional Bill - IPD Visit</span>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 max-h-[calc(100vh-120px)] overflow-y-auto">
            {isLoading ? (
              <LoaderComponent />
            ) : billingData ? (
              <div className="space-y-6">
                {/* Pharmacy Section */}
                {billingData.pharmacy.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Pharmacy Orders
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      {billingData.pharmacy.map((sale) => (
                        <div key={sale.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <span className="font-medium text-gray-700">
                                Sale #{sale.sale_number}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">
                                {formatDate(sale.sale_date)}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(sale.net_amount)}
                            </span>
                          </div>
                          <div className="ml-4 mt-2 space-y-1">
                            {sale.items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-sm text-gray-600"
                              >
                                <span>
                                  {item.name} × {item.quantity}
                                </span>
                                <span>{formatCurrency(item.total)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-end pt-2 border-t">
                        <div className="text-right">
                          <span className="text-sm text-gray-600">Subtotal: </span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(calculatePharmacyTotal())}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lab Orders Section */}
                {billingData.labOrders.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Lab Orders
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      {billingData.labOrders.map((order) => (
                        <div key={order.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <span className="font-medium text-gray-700">
                                Order #{order.order_number}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">
                                {formatDate(order.order_date)}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(order.total_amount)}
                            </span>
                          </div>
                          <div className="ml-4 mt-2 space-y-1">
                            {order.items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-sm text-gray-600"
                              >
                                <span>{item.name}</span>
                                <span>{formatCurrency(item.fee)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-end pt-2 border-t">
                        <div className="text-right">
                          <span className="text-sm text-gray-600">Subtotal: </span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(calculateLabTotal())}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Services Section */}
                {billingData.services.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Visit Services
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="space-y-2">
                        {billingData.services.map((service) => (
                          <div
                            key={service.id}
                            className="flex justify-between text-sm"
                          >
                            <span className="text-gray-700">
                              {service.name} {service.quantity > 1 && `× ${service.quantity}`}
                            </span>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(service.total)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end pt-2 mt-4 border-t">
                        <div className="text-right">
                          <span className="text-sm text-gray-600">Subtotal: </span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(calculateServicesTotal())}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Patient Balance Section */}
                <div className="border rounded-lg bg-blue-50">
                  <div className="p-4 bg-blue-100 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Patient Balance
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Transactions:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(billingData.patientBalance.total_transactions)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Allocations:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(billingData.patientBalance.total_allocations)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-200">
                      <span className="font-semibold text-gray-900">Balance:</span>
                      <span
                        className={`font-bold text-lg ${
                          billingData.patientBalance.balance >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatCurrency(billingData.patientBalance.balance)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Grand Total */}
                <div className="border-2 rounded-lg bg-gray-50">
                  <div className="p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-900">
                        Grand Total:
                      </span>
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(calculateGrandTotal())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No billing data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IPDBillingModal;

